import { DeviceService } from '@flipp/shopper-ad-web-shared';

function setStyleForAdContainers(
  parentElement: Readonly<Document>,
  style: string,
) {
  setTimeout(() => {
    parentElement
      .querySelectorAll('[data-freestar-ad], [data-google-query-id]')
      .forEach(container => {
        if (
          container.children.length === 1 &&
          container
            .querySelector('iframe')
            ?.contentDocument?.querySelector('.flippxp-frame')
        ) {
          container.setAttribute('style', style);
        }
      });
  }, 500);
}

function setStyleForRootElements(
  parentElement: Readonly<Document>,
  style: string,
  rootElement: string,
) {
  rootElement.split(',').forEach(selector => {
    const element = parentElement?.querySelector(selector) as HTMLElement;
    if (element?.children.length === 1) {
      element.setAttribute('style', style);
    }
  });
}

// Walks up to the publisher page to apply a callback to the outer GAM iframe.
// Silently no-ops in SafeFrame (cross-origin) environments.
function applyToOuterGAMFrame(fn: (el: HTMLElement) => void) {
  try {
    const outerFrame = window.parent.frameElement as HTMLElement | null;
    if (outerFrame) fn(outerFrame);
  } catch {
    // Cross-origin (SafeFrame) — outer frame not accessible
  }
}

function resizeGamContainer(width: number, height: number) {
  const iframe = window.frameElement as HTMLElement | null;
  if (!iframe) return;
  const apply = (el: HTMLElement | null | undefined) => {
    if (!el) return;
    el.style.setProperty('width', `${width}px`, 'important');
    el.style.setProperty('height', `${height}px`, 'important');
    el.style.setProperty('max-width', `${width}px`, 'important');
    el.style.setProperty('max-height', `${height}px`, 'important');
    el.style.setProperty('display', 'block', 'important');
    el.style.setProperty('margin-left', 'auto', 'important');
    el.style.setProperty('margin-right', 'auto', 'important');
  };
  apply(iframe.parentElement);
  apply(iframe);
  applyToOuterGAMFrame(outerFrame => {
    apply(outerFrame);
    apply(outerFrame.parentElement);
  });
}

type SetStyleForIframeContainersParams = {
  style: string;
  decisionExist: boolean;
  callSlotFillResultCallback: (arg0: boolean) => void;
  nestedIframeResizing: boolean;
  gamContainerWidth?: number;
};

function setStyleForIframeContainers({
  style,
  decisionExist,
  callSlotFillResultCallback,
  nestedIframeResizing,
  gamContainerWidth,
}: Readonly<SetStyleForIframeContainersParams>) {
  const iframe = window.frameElement;
  if (!iframe) return;

  const parentElement = iframe.parentElement;
  const containerElement = parentElement?.parentElement;
  const isSingleChild =
    parentElement?.children.length === 1 &&
    containerElement?.children.length === 1;
  const platform = DeviceService.getDeviceInfo().type;

  if (parentElement && decisionExist) {
    const isDesktopOverride =
      gamContainerWidth && gamContainerWidth > 0 && platform !== 'mobile';
    const width = isDesktopOverride ? `${gamContainerWidth}px` : '100%';
    parentElement.style.width = width;
    if (isDesktopOverride) {
      (iframe as HTMLElement).style.width = width;
      applyToOuterGAMFrame(outerFrame => {
        outerFrame.style.width = width;
        if (outerFrame.parentElement)
          outerFrame.parentElement.style.width = width;
      });
    }
  }

  if (!decisionExist && containerElement && isSingleChild) {
    containerElement.style.display = 'none';
    callSlotFillResultCallback(false);
    return;
  }

  if (nestedIframeResizing && containerElement && isSingleChild) {
    if (containerElement.parentElement?.children.length === 1) {
      containerElement.parentElement.setAttribute('style', style);
    }
    containerElement.setAttribute('style', style);
    applyToOuterGAMFrame(outerFrame => {
      outerFrame.setAttribute('style', style);
      outerFrame.parentElement?.setAttribute('style', style);
    });
  }
}

export {
  resizeGamContainer,
  setStyleForAdContainers,
  setStyleForIframeContainers,
  setStyleForRootElements,
};
